/**
 * @file Upgrades Power/Speed/Money : niveaux, couts, achat unitaire / x10 / max, achat maintenu (cote UI), auto-upgrade.
 */
import type { Decimal } from '@shared/bignum';
import { UPGRADE_IDS } from '@shared/enums';
import type { UpgradeId } from '@shared/enums';
import { fail, ok } from '@shared/types';
import type { Result } from '@shared/types';
import type { SimContext } from '../Context';
import { upgradeCost, upgradeCostRange, upgradeMaxAffordable, upgradeMultiplier } from '../balance/formulas';

export class UpgradeService {
  private autoAcc = 0;

  constructor(private readonly ctx: SimContext) {}

  level(id: UpgradeId): number {
    return this.ctx.data.run.upgrades[id];
  }

  isMax(id: UpgradeId): boolean {
    return this.level(id) >= this.ctx.config.balance.upgrades.maxLevel;
  }

  cost(id: UpgradeId): Decimal {
    return upgradeCost(this.ctx.config.balance, id, this.level(id));
  }

  costFor(id: UpgradeId, count: number): Decimal {
    return upgradeCostRange(this.ctx.config.balance, id, this.level(id), count);
  }

  maxAffordable(id: UpgradeId): number {
    return upgradeMaxAffordable(this.ctx.config.balance, id, this.level(id), this.ctx.wallet.get('money'));
  }

  multiplier(id: UpgradeId, level = this.level(id)): Decimal {
    return upgradeMultiplier(this.ctx.config.balance.upgrades.specs[id], level);
  }

  /** Achat de `count` niveaux (ou 'max' = tout ce qui est abordable). Debit atomique, stats invalidees, mission comptee. */
  buy(id: UpgradeId, count: number | 'max', auto = false): Result<{ count: number }> {
    const ctx = this.ctx;
    if (this.isMax(id)) return fail('maxLevel');
    const room = ctx.config.balance.upgrades.maxLevel - this.level(id);
    const n = count === 'max' ? this.maxAffordable(id) : Math.min(Math.floor(count), room);
    if (!Number.isFinite(n) || (count !== 'max' && n < 1)) return fail('invalidArgument');
    if (n < 1) return fail('notEnoughFunds');
    const cost = this.costFor(id, n);
    return ctx.tx.purchase({ money: cost }, `upgrade:${id}`, () => {
      ctx.data.run.upgrades[id] += n;
      ctx.bus.emit('ModifiersDirty', { source: 'upgrades' });
      ctx.bus.emit('UpgradeBought', { id, level: ctx.data.run.upgrades[id], count: n, cost, ...(auto ? { auto: true } : {}) });
      ctx.services.missions.increment('upgradesBought', n);
      return ok({ count: n });
    });
  }

  /**
   * Si settings.autoUpgrade : toutes les `upgrades.auto.intervalSeconds`, achete jusqu'a `maxLevelsPerTick` niveaux, toujours l'upgrade la moins
   * chere abordable (egalite : power > speed > money). Le plan est calcule a l'avance (niveaux et argent simules) puis achete par lots :
   * un evenement UpgradeBought par upgrade au plus, au lieu d'un par niveau.
   */
  autoUpgrade(dt: number): void {
    if (!this.ctx.data.settings.autoUpgrade) return;
    const { intervalSeconds, maxLevelsPerTick } = this.ctx.config.balance.upgrades.auto;
    this.autoAcc += dt;
    if (this.autoAcc < intervalSeconds) return;
    this.autoAcc %= intervalSeconds;
    const bal = this.ctx.config.balance;
    const levels = { ...this.ctx.data.run.upgrades };
    let money = this.ctx.wallet.get('money');
    const plan: Record<UpgradeId, number> = { power: 0, speed: 0, money: 0 };
    for (let k = 0; k < maxLevelsPerTick; k++) {
      let best: UpgradeId | null = null;
      let bestCost: Decimal | null = null;
      for (const id of UPGRADE_IDS) {
        if (levels[id] >= bal.upgrades.maxLevel) continue;
        const c = upgradeCost(bal, id, levels[id]);
        if (money.lt(c)) continue;
        if (bestCost === null || c.lt(bestCost)) {
          best = id;
          bestCost = c;
        }
      }
      if (!best || !bestCost) break;
      money = money.minus(bestCost);
      levels[best]++;
      plan[best]++;
    }
    for (const id of UPGRADE_IDS) if (plan[id] > 0) this.buy(id, plan[id], true);
  }
}
